<template>
  <v-dialog v-model="dialog" max-width="1400px" persistent scrollable>
    <v-card>
      <v-toolbar density="compact" color="deep-purple" dark>
        <v-toolbar-title class="text-subtitle-1">
          <v-icon size="small">mdi-history</v-icon>
          {{ $t('projects.history.title') }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-switch
          v-if="!loading && history && history.length > 0 && currentIndex > 0"
          v-model="showComparison"
          :label="$t('projects.history.showChanges')"
          color="white"
          hide-details
          density="compact"
          class="mr-4"
        ></v-switch>
        <v-switch
          v-if="!loading && history && history.length > 0"
          v-model="restoreMode"
          :label="$t('projects.history.restoreMode')"
          color="warning"
          hide-details
          density="compact"
          :disabled="!canRestore"
          @update:modelValue="onRestoreModeChange"
          class="mr-4"
        ></v-switch>
        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pt-2" style="height: 75vh;">
        <!-- Loading State -->
        <div v-if="loading" class="d-flex justify-center align-center" style="height: 100%;">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>

        <!-- No History State -->
        <div v-else-if="!history || history.length === 0" class="d-flex flex-column justify-center align-center" style="height: 100%;">
          <v-icon size="48" color="grey">mdi-history</v-icon>
          <p class="text-subtitle-1 mt-2 text-grey">{{ $t('projects.history.noHistory') }}</p>
        </div>

        <!-- History Navigation -->
        <div v-else>
          <!-- Navigation Controls -->
          <v-card outlined class="mb-2">
            <v-card-text class="py-2">
              <div class="d-flex align-center justify-space-between">
                <v-btn
                  icon
                  @click="navigateToPrevious"
                  :disabled="currentIndex === 0"
                  size="small"
                >
                  <v-icon>mdi-chevron-left</v-icon>
                </v-btn>

                <div class="text-center flex-grow-1">
                  <div class="text-subtitle-2">
                    {{ $t('projects.history.version') }} {{ currentIndex + 1 }} / {{ history.length }}
                    <v-chip 
                      v-if="currentIndex === history.length - 1" 
                      size="x-small" 
                      color="success" 
                      class="ml-2"
                    >
                      {{ $t('projects.history.current') }}
                    </v-chip>
                  </div>
                  <div class="text-caption text-grey">
                    {{ formatDateTime(currentHistoryEntry.timestamp) }}
                  </div>
                  <v-chip size="x-small" color="primary" class="mt-1">
                    <v-icon size="x-small">mdi-account</v-icon>
                    {{ currentHistoryEntry.modifiedBy }}
                  </v-chip>
                </div>

                <v-btn
                  icon
                  @click="navigateToNext"
                  :disabled="currentIndex === history.length - 1"
                  size="small"
                >
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
              </div>

              <!-- Timeline -->
              <v-slider
                v-model="currentIndex"
                :min="0"
                :max="history.length - 1"
                :step="1"
                show-ticks="always"
                tick-size="3"
                class="mt-2"
                color="deep-purple"
                track-color="grey-lighten-2"
                density="compact"
              ></v-slider>
            </v-card-text>
          </v-card>

          <!-- Restore Action Card -->
          <v-card outlined class="mb-2" v-if="restoreMode && selectedFields.size > 0" color="warning-lighten-5">
            <v-card-text class="py-2">
              <v-row align="center">
                <v-col cols="12" md="8">
                  <div class="text-subtitle-2 font-weight-bold">
                    <v-icon size="small" color="warning" class="mr-2">mdi-restore</v-icon>
                    {{ $t('projects.history.restoreSelected', { count: selectedFields.size }) }}
                  </div>
                  <div class="text-caption text-grey">
                    {{ $t('projects.history.restoreWarning') }}
                  </div>
                </v-col>
                <v-col cols="12" md="4" class="text-right">
                  <v-btn
                    color="warning"
                    @click="confirmRestore"
                    prepend-icon="mdi-restore"
                    :loading="restoring"
                  >
                    {{ $t('projects.history.confirmRestore') }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Fields Display -->
          <v-card outlined>
            <v-card-text>
              <v-row>
                <v-col
                  v-for="(field, fieldName) in displayFields"
                  :key="fieldName"
                  cols="12"
                  sm="6"
                  md="4"
                  lg="3"
                >
                  <div
                    :class="{
                      'history-field': true,
                      'field-changed': showComparison && isFieldChanged(fieldName),
                      'field-selected': restoreMode && selectedFields.has(fieldName),
                      'pa-2': true,
                      'rounded': true
                    }">
                  >
                    <div class="d-flex align-center justify-space-between mb-1">
                      <div class="text-caption text-grey">
                        {{ getFieldLabel(fieldName) }}
                        <v-chip
                          v-if="showComparison && isFieldChanged(fieldName)"
                          size="x-small"
                          color="warning"
                          class="ml-2"
                        >
                          {{ $t('projects.history.modified') }}
                        </v-chip>
                      </div>
                      <v-checkbox
                        v-if="restoreMode && !isSystemField(fieldName)"
                        v-model="selectedFieldsArray"
                        :value="fieldName"
                        color="warning"
                        hide-details
                        density="compact"
                        class="mt-0"
                      ></v-checkbox>
                    </div>
                    <div class="text-body-2">
                      <!-- Special rendering for notes field -->
                      <span v-if="isNotesField(fieldName)">
                        <div v-if="Array.isArray(getCurrentValue(fieldName)) && getCurrentValue(fieldName).length > 0">
                          <v-list dense class="notes-list pa-0">
                            <v-list-item
                              v-for="(note, idx) in getCurrentValue(fieldName)"
                              :key="idx"
                              class="note-item px-2 py-1"
                            >
                              <v-list-item-title class="text-body-2">
                                <v-chip size="x-small" :color="getStatusColor(note.statusId)" class="mr-2">
                                  {{ note.statusDescription || `Status ${note.statusId}` }}
                                </v-chip>
                                {{ note.text }}
                              </v-list-item-title>
                              <v-list-item-subtitle class="text-caption">
                                {{ note.user }} - {{ formatNoteDate(note.timestamp) }}
                              </v-list-item-subtitle>
                            </v-list-item>
                          </v-list>
                        </div>
                        <span v-else class="text-grey">{{ $t('projects.history.empty') }}</span>
                      </span>
                      <!-- Standard comparison view for other fields -->
                      <span v-else-if="showComparison && isFieldChanged(fieldName)">
                        <span class="old-value text-decoration-line-through text-grey">
                          {{ formatFieldValue(fieldName, getPreviousValue(fieldName)) }}
                        </span>
                        <v-icon size="small" class="mx-2">mdi-arrow-right</v-icon>
                        <span class="new-value font-weight-bold">
                          {{ formatFieldValue(fieldName, getCurrentValue(fieldName)) }}
                        </span>
                      </span>
                      <span v-else>
                        {{ formatFieldValue(fieldName, getCurrentValue(fieldName)) }}
                      </span>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Changed Fields Summary -->
          <v-card outlined class="mt-4" v-if="showComparison && changedFieldsCount > 0">
            <v-card-title class="text-subtitle-1">
              <v-icon left color="warning">mdi-alert-circle</v-icon>
              {{ $t('projects.history.changedFields') }}: {{ changedFieldsCount }}
            </v-card-title>
            <v-card-text>
              <v-chip
                v-for="fieldName in changedFieldsList"
                :key="fieldName"
                size="small"
                color="warning"
                class="mr-2 mb-2"
              >
                {{ getFieldLabel(fieldName) }}
              </v-chip>
            </v-card-text>
          </v-card>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" variant="text" @click="closeDialog">
          {{ $t('common.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useProjectStore } from '../stores/projects.js'
import { useAuthStore } from '../stores/auth.js'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const projectStore = useProjectStore()
const authStore = useAuthStore()

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  projectId: {
    type: String,
    default: null
  },
  currentProject: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'restored'])

const dialog = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const loading = ref(false)
const history = ref([])
const currentIndex = ref(0)
const showComparison = ref(true)
const restoreMode = ref(false)
const restoring = ref(false)
const selectedFieldsArray = ref([])

// Computed set for faster lookups
const selectedFields = computed(() => new Set(selectedFieldsArray.value))

// Check if user can restore (admin or superadmin)
const canRestore = computed(() => {
  const user = authStore.user
  const userRole = user?.role
  
  // Debug log
  console.log('ProjectHistory - Auth user:', user)
  console.log('ProjectHistory - User role:', userRole)
  console.log('ProjectHistory - Can restore:', userRole === 'admin' || userRole === 'superadmin')
  
  return userRole === 'admin' || userRole === 'superadmin'
})

// Current history entry being displayed
const currentHistoryEntry = computed(() => {
  if (!history.value || history.value.length === 0) return null
  return history.value[currentIndex.value]
})

// Previous history entry for comparison
const previousHistoryEntry = computed(() => {
  if (!history.value || history.value.length === 0 || currentIndex.value === 0) return null
  return history.value[currentIndex.value - 1]
})

// Fields to display (from fields.json configuration)
const displayFields = computed(() => {
  if (!currentHistoryEntry.value?.data) return {}
  
  const fields = {}
  const fieldsConfig = projectStore.fields || []
  
  if (Array.isArray(fieldsConfig)) {
    for (const field of fieldsConfig) {
      // Skip system fields we don't want to show in history
      if (['createdAt', 'createdBy', 'updatedAt', 'updatedBy'].includes(field.name)) {
        continue
      }
      fields[field.name] = field
    }
  }
  
  return fields
})

// Get current value of a field
const getCurrentValue = (fieldName) => {
  if (!currentHistoryEntry.value?.data) return ''
  return currentHistoryEntry.value.data[fieldName] ?? ''
}

// Get previous value of a field
const getPreviousValue = (fieldName) => {
  if (!previousHistoryEntry.value?.data) return ''
  return previousHistoryEntry.value.data[fieldName] ?? ''
}

// Check if a field has changed
const isFieldChanged = (fieldName) => {
  if (currentIndex.value === 0) return false
  const currentValue = getCurrentValue(fieldName)
  const previousValue = getPreviousValue(fieldName)
  return JSON.stringify(currentValue) !== JSON.stringify(previousValue)
}

// List of changed fields
const changedFieldsList = computed(() => {
  if (currentIndex.value === 0) return []
  const changed = []
  for (const fieldName of Object.keys(displayFields.value)) {
    if (isFieldChanged(fieldName)) {
      changed.push(fieldName)
    }
  }
  return changed
})

// Count of changed fields
const changedFieldsCount = computed(() => changedFieldsList.value.length)

// Navigation methods
const navigateToPrevious = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--
  }
}

const navigateToNext = () => {
  if (currentIndex.value < history.value.length - 1) {
    currentIndex.value++
  }
}

// Check if field is a system field that should not be restored
const isSystemField = (fieldName) => {
  return ['idProject', 'createdAt', 'createdBy', 'updatedAt', 'updatedBy', 'version'].includes(fieldName)
}

// Handle restore mode change
const onRestoreModeChange = (value) => {
  if (!value) {
    // Clear selection when exiting restore mode
    selectedFieldsArray.value = []
  }
}

// Confirm and execute restore
const confirmRestore = async () => {
  if (selectedFieldsArray.value.length === 0) return
  
  try {
    restoring.value = true
    
    // Prepare data to restore
    const restoreData = {}
    for (const fieldName of selectedFieldsArray.value) {
      restoreData[fieldName] = getCurrentValue(fieldName)
    }
    
    // Include version for concurrency check
    if (props.currentProject?.version !== undefined) {
      restoreData.version = props.currentProject.version
    }
    
    // Call update API
    await projectStore.updateProject(props.projectId, restoreData)
    
    // Emit success event
    emit('restored', { fields: selectedFieldsArray.value })
    
    // Close dialog
    closeDialog()
    
  } catch (error) {
    console.error('Error restoring fields:', error)
    // Handle concurrency conflict or other errors
    alert(t('projects.history.restoreError'))
  } finally {
    restoring.value = false
  }
}

// Format field value for display
const formatFieldValue = (fieldName, value) => {
  if (value === null || value === undefined || value === '') {
    return t('projects.history.empty')
  }
  
  const fieldConfig = displayFields.value[fieldName]
  
  // Special handling for notes field (array)
  if (fieldConfig?.type === 'notelist' && Array.isArray(value)) {
    return `${value.length} ${value.length === 1 ? 'nota' : 'notas'}`
  }
  
  // Format based on field type
  if (fieldConfig?.type === 'date') {
    try {
      // Se la data è in formato YYYY-MM-DD, parsala come data locale
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split('-').map(Number)
        const date = new Date(year, month - 1, day) // month è 0-indexed
        return date.toLocaleDateString('it-IT')
      }
      const date = new Date(value)
      return date.toLocaleDateString('it-IT')
    } catch {
      return value
    }
  }
  
  // Format select fields
  if (fieldConfig?.type === 'select') {
    if (fieldName === 'projectType') {
      const type = projectStore.projectTypes.find(t => String(t.id) === String(value))
      return type?.descr || value
    }
    if (fieldName === 'activeStatus') {
      const status = projectStore.statusList.find(s => String(s.id) === String(value))
      return status ? `${status.icon} ${status.descr}` : value
    }
    if (fieldConfig.options) {
      const option = fieldConfig.options.find(o => o.value === value)
      return option?.text || value
    }
  }
  
  return value
}

// Check if field is notes field
const isNotesField = (fieldName) => {
  const fieldConfig = displayFields.value[fieldName]
  return fieldConfig?.type === 'notelist'
}

// Get status color for notes
const getStatusColor = (statusId) => {
  if (!statusId) return 'grey'
  const status = projectStore.statusList.find(s => s.id === statusId)
  return status?.color || 'grey'
}

// Format note timestamp
const formatNoteDate = (timestamp) => {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Format date and time
const formatDateTime = (timestamp) => {
  if (!timestamp) return ''
  const date = new Date(timestamp)
  // Usa formato locale senza conversioni UTC
  return date.toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Get field label
const getFieldLabel = (fieldName) => {
  return t(`projects.fields.${fieldName}`)
}

// Load history when dialog opens
watch(() => props.modelValue, async (newValue) => {
  if (newValue && props.projectId) {
    await loadHistory()
  }
})

// Load history data
const loadHistory = async () => {
  if (!props.projectId) return
  
  try {
    loading.value = true
    const historyData = await projectStore.getProjectHistory(props.projectId)
    
    if (historyData && historyData.history) {
      // Sort history by timestamp (oldest first)
      const sortedHistory = [...historyData.history].sort((a, b) => {
        return new Date(a.timestamp) - new Date(b.timestamp)
      })
      
      // Add current project state as the most recent entry
      if (props.currentProject) {
        sortedHistory.push({
          timestamp: props.currentProject.updatedAt || new Date().toISOString(),
          modifiedBy: props.currentProject.updatedBy || props.currentProject.createdBy || 'unknown',
          data: props.currentProject
        })
      }
      
      history.value = sortedHistory
      
      // Start at the most recent (last) entry
      currentIndex.value = history.value.length - 1
    } else {
      history.value = []
    }
  } catch (error) {
    console.error('Error loading history:', error)
    history.value = []
  } finally {
    loading.value = false
  }
}

const closeDialog = () => {
  dialog.value = false
  history.value = []
  currentIndex.value = 0
  showComparison.value = true
  restoreMode.value = false
  restoring.value = false
  selectedFieldsArray.value = []
}
</script>

<style scoped>
.history-field {
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background-color: rgba(0, 0, 0, 0.02);
}

.field-changed {
  background-color: rgba(255, 193, 7, 0.1);
  border-color: rgba(255, 193, 7, 0.5);
  border-width: 2px;
}

.field-selected {
  background-color: rgba(255, 152, 0, 0.15);
  border-color: rgba(255, 152, 0, 0.8);
  border-width: 2px;
}

.old-value {
  opacity: 0.7;
}

.new-value {
  color: rgb(var(--v-theme-primary));
}

:deep(.v-slider-thumb) {
  width: 20px;
  height: 20px;
}

.notes-list {
  background-color: rgba(0, 0, 0, 0.02);
  border-radius: 4px;
  max-height: 200px;
  overflow-y: auto;
}

.note-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.note-item:last-child {
  border-bottom: none;
}
</style>
